// Code generated by OpenAPI Generator (https://openapi-generator.tech), manual changes will be lost - read more on https://github.com/algolia/api-clients-automation. DO NOT EDIT.

import type {
  CreateClientOptions,
  Headers,
  Host,
  QueryParameters,
  Request,
  RequestOptions,
  ServerSentEvent,
  StreamEvent,
} from '@algolia/client-common';
import { createAuth, createTransporter, getAlgoliaAgent, shuffle, validateRequired } from '@algolia/client-common';

import type { AgentConfigCreate } from '../model/agentConfigCreate';
import type { AgentWithVersionResponse } from '../model/agentWithVersionResponse';
import type { AllowedDomainListResponse } from '../model/allowedDomainListResponse';
import type { AllowedDomainResponse } from '../model/allowedDomainResponse';
import type { ApplicationConfigPatch } from '../model/applicationConfigPatch';
import type { ApplicationConfigResponse } from '../model/applicationConfigResponse';
import type { ConversationFullResponse } from '../model/conversationFullResponse';
import type { FeedbackCreationRequest } from '../model/feedbackCreationRequest';
import type { FeedbackResponse } from '../model/feedbackResponse';
import type { PaginatedAgentsResponse } from '../model/paginatedAgentsResponse';
import type { PaginatedConversationsResponse } from '../model/paginatedConversationsResponse';
import type { PaginatedProviderAuthenticationsResponse } from '../model/paginatedProviderAuthenticationsResponse';
import type { PaginatedSecretKeysResponse } from '../model/paginatedSecretKeysResponse';
import type { ProviderAuthenticationCreate } from '../model/providerAuthenticationCreate';
import type { ProviderAuthenticationResponse } from '../model/providerAuthenticationResponse';
import type { SecretKeyCreate } from '../model/secretKeyCreate';
import type { SecretKeyResponse } from '../model/secretKeyResponse';
import type { UserDataResponse } from '../model/userDataResponse';

import type {
  BulkCreateAllowedDomainsProps,
  BulkDeleteAllowedDomainsProps,
  CreateAgentAllowedDomainProps,
  CreateAgentCompletionProps,
  CustomDeleteProps,
  CustomGetProps,
  CustomPostProps,
  CustomPutProps,
  DeleteAgentConversationsProps,
  DeleteAgentProps,
  DeleteAllowedDomainProps,
  DeleteConversationProps,
  DeleteProviderProps,
  DeleteSecretKeyProps,
  DeleteUserDataProps,
  ExportConversationsProps,
  GetAgentProps,
  GetAllowedDomainProps,
  GetConversationProps,
  GetProviderProps,
  GetSecretKeyProps,
  GetUserDataProps,
  InvalidateAgentCacheProps,
  ListAgentAllowedDomainsProps,
  ListAgentConversationsProps,
  ListAgentsProps,
  ListProviderModelsProps,
  ListProvidersProps,
  ListSecretKeysProps,
  PublishAgentProps,
  UnpublishAgentProps,
  UpdateAgentProps,
  UpdateProviderProps,
  UpdateSecretKeyProps,
} from '../model/clientMethodProps';

export const apiClientVersion = '1.0.0';

function getDefaultHosts(appId: string): Host[] {
  return (
    [
      {
        url: `${appId}-dsn.algolia.net`,
        accept: 'read',
        protocol: 'https',
      },
      {
        url: `${appId}.algolia.net`,
        accept: 'write',
        protocol: 'https',
      },
    ] as Host[]
  ).concat(
    shuffle([
      {
        url: `${appId}-1.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
      {
        url: `${appId}-2.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
      {
        url: `${appId}-3.algolianet.com`,
        accept: 'readWrite',
        protocol: 'https',
      },
    ]),
  );
}

export function createAgentStudioClient({
  appId: appIdOption,
  apiKey: apiKeyOption,
  authMode,
  algoliaAgents,
  ...options
}: CreateClientOptions) {
  const auth = createAuth(appIdOption, apiKeyOption, authMode);
  const transporter = createTransporter({
    hosts: getDefaultHosts(appIdOption),
    ...options,
    algoliaAgent: getAlgoliaAgent({
      algoliaAgents,
      client: 'AgentStudio',
      version: apiClientVersion,
    }),
    baseHeaders: {
      'content-type': 'text/plain',
      ...auth.headers(),
      ...options.baseHeaders,
    },
    baseQueryParameters: {
      ...auth.queryParameters(),
      ...options.baseQueryParameters,
    },
  });

  return {
    transporter,

    /**
     * The `appId` currently in use.
     */
    appId: appIdOption,

    /**
     * The `apiKey` currently in use.
     */
    apiKey: apiKeyOption,

    /**
     * Clears the cache of the transporter for the `requestsCache` and `responsesCache` properties.
     */
    clearCache(): Promise<void> {
      return Promise.all([transporter.requestsCache.clear(), transporter.responsesCache.clear()]).then(() => undefined);
    },

    /**
     * Get the value of the `algoliaAgent`, used by our libraries internally and telemetry system.
     */
    get _ua(): string {
      return transporter.algoliaAgent.value;
    },

    /**
     * Adds a `segment` to the `x-algolia-agent` sent with every requests.
     *
     * @param segment - The algolia agent (user-agent) segment to add.
     * @param version - The version of the agent.
     */
    addAlgoliaAgent(segment: string, version?: string | undefined): void {
      transporter.algoliaAgent.add({ segment, version });
    },

    /**
     * Helper method to switch the API key used to authenticate the requests.
     *
     * @param params - Method params.
     * @param params.apiKey - The new API Key to use.
     */
    setClientApiKey({ apiKey }: { apiKey: string }): void {
      if (!authMode || authMode === 'WithinHeaders') {
        transporter.baseHeaders['x-algolia-api-key'] = apiKey;
      } else {
        transporter.baseQueryParameters['x-algolia-api-key'] = apiKey;
      }
    },

    /**
     * Add multiple allowed domain patterns. Duplicates are skipped.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param bulkCreateAllowedDomains - The bulkCreateAllowedDomains object.
     * @param bulkCreateAllowedDomains.agentId - The agentId.
     * @param bulkCreateAllowedDomains.allowedDomainBulkInsert - The allowedDomainBulkInsert object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    bulkCreateAllowedDomains(
      { agentId, allowedDomainBulkInsert }: BulkCreateAllowedDomainsProps,
      requestOptions?: RequestOptions,
    ): Promise<AllowedDomainListResponse> {
      validateRequired('agentId', 'bulkCreateAllowedDomains', agentId);

      validateRequired('allowedDomainBulkInsert', 'bulkCreateAllowedDomains', allowedDomainBulkInsert);

      validateRequired('allowedDomainBulkInsert.domains', 'bulkCreateAllowedDomains', allowedDomainBulkInsert.domains);

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains/bulk'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: allowedDomainBulkInsert,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Delete allowed domains by id list.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param bulkDeleteAllowedDomains - The bulkDeleteAllowedDomains object.
     * @param bulkDeleteAllowedDomains.agentId - The agentId.
     * @param bulkDeleteAllowedDomains.allowedDomainBulkDelete - The allowedDomainBulkDelete object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    bulkDeleteAllowedDomains(
      { agentId, allowedDomainBulkDelete }: BulkDeleteAllowedDomainsProps,
      requestOptions?: RequestOptions,
    ): Promise<void> {
      validateRequired('agentId', 'bulkDeleteAllowedDomains', agentId);

      validateRequired('allowedDomainBulkDelete', 'bulkDeleteAllowedDomains', allowedDomainBulkDelete);

      validateRequired(
        'allowedDomainBulkDelete.domainIds',
        'bulkDeleteAllowedDomains',
        allowedDomainBulkDelete.domainIds,
      );

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains/bulk'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
        data: allowedDomainBulkDelete,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Create a new agent.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param agentConfigCreate - The agentConfigCreate object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createAgent(
      agentConfigCreate: AgentConfigCreate,
      requestOptions?: RequestOptions,
    ): Promise<AgentWithVersionResponse> {
      validateRequired('agentConfigCreate', 'createAgent', agentConfigCreate);

      validateRequired('agentConfigCreate.name', 'createAgent', agentConfigCreate.name);
      validateRequired('agentConfigCreate.instructions', 'createAgent', agentConfigCreate.instructions);

      const requestPath = '/agent-studio/1/agents';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: agentConfigCreate,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Add a single allowed domain pattern (e.g. https://app.example.com or *.example.com).
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param createAgentAllowedDomain - The createAgentAllowedDomain object.
     * @param createAgentAllowedDomain.agentId - The agentId.
     * @param createAgentAllowedDomain.allowedDomainCreate - The allowedDomainCreate object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createAgentAllowedDomain(
      { agentId, allowedDomainCreate }: CreateAgentAllowedDomainProps,
      requestOptions?: RequestOptions,
    ): Promise<AllowedDomainResponse> {
      validateRequired('agentId', 'createAgentAllowedDomain', agentId);

      validateRequired('allowedDomainCreate', 'createAgentAllowedDomain', allowedDomainCreate);

      validateRequired('allowedDomainCreate.domain', 'createAgentAllowedDomain', allowedDomainCreate.domain);

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: allowedDomainCreate,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Create a completion for the specified agent.  This endpoint handles two types of requests: 1. Normal completion request: User message -> Agent response 2. Tool approval response: User approval -> Execute tool -> Agent response  Tool Approval Flow (for MCP tools with requiresApproval: true): - Request 1: User sends message -> Agent requests tool call -> Return approval request - Request 2: User approves -> Execute tool -> Agent continues with result.
     *
     * Required API Key ACLs:
     *  - search
     * @param createAgentCompletion - The createAgentCompletion object.
     * @param createAgentCompletion.agentId - The agentId.
     * @param createAgentCompletion.compatibilityMode - Compatibility mode for the completion API.
     * @param createAgentCompletion.agentCompletionRequest - The agentCompletionRequest object.
     * @param createAgentCompletion.stream - Whether to stream the response or not.
     * @param createAgentCompletion.cache - Use cached responses if available.
     * @param createAgentCompletion.memory - Set to false to disable memory (enabled by default).
     * @param createAgentCompletion.analytics - Set to false to skip analytics for this completion (default: true). Disables Agent Studio BigQuery analytics, Algolia search analytics, click analytics, and query-suggestions training. Useful for offline-eval workflows.
     * @param createAgentCompletion.xAlgoliaSecureUserToken - The X-Algolia-Secure-User-Token.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createAgentCompletion(
      {
        agentId,
        compatibilityMode,
        agentCompletionRequest,
        stream,
        cache,
        memory,
        analytics,
        xAlgoliaSecureUserToken,
      }: CreateAgentCompletionProps,
      requestOptions?: RequestOptions,
    ): Promise<{ [key: string]: any }> {
      validateRequired('agentId', 'createAgentCompletion', agentId);

      validateRequired('compatibilityMode', 'createAgentCompletion', compatibilityMode);

      validateRequired('agentCompletionRequest', 'createAgentCompletion', agentCompletionRequest);

      const requestPath = '/agent-studio/1/agents/{agentId}/completions'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (compatibilityMode !== undefined) {
        queryParameters['compatibilityMode'] = compatibilityMode.toString();
      }

      if (stream !== undefined) {
        queryParameters['stream'] = stream.toString();
      }

      if (cache !== undefined) {
        queryParameters['cache'] = cache.toString();
      }

      if (memory !== undefined) {
        queryParameters['memory'] = memory.toString();
      }

      if (analytics !== undefined) {
        queryParameters['analytics'] = analytics.toString();
      }

      if (xAlgoliaSecureUserToken !== undefined) {
        headers['X-Algolia-Secure-User-Token'] = xAlgoliaSecureUserToken.toString();
      }

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: agentCompletionRequest,
      };

      return transporter.request(request, requestOptions);
    },
    /**
     * Create a completion for the specified agent.  This endpoint handles two types of requests: 1. Normal completion request: User message -> Agent response 2. Tool approval response: User approval -> Execute tool -> Agent response  Tool Approval Flow (for MCP tools with requiresApproval: true): - Request 1: User sends message -> Agent requests tool call -> Return approval request - Request 2: User approves -> Execute tool -> Agent continues with result. (raw streaming version).
     *
     * Yields raw {@link ServerSentEvent} objects. Each event's `data` field contains a JSON-encoded `{ [key: string]: any; }` string.
     *
     * @see createAgentCompletionStream for the parsed variant.
     * @see createAgentCompletion for the non-streaming version.
     */
    createAgentCompletionStreamRaw(
      {
        agentId,
        compatibilityMode,
        agentCompletionRequest,
        stream,
        cache,
        memory,
        analytics,
        xAlgoliaSecureUserToken,
      }: CreateAgentCompletionProps,
      requestOptions?: RequestOptions,
    ): AsyncGenerator<ServerSentEvent> {
      validateRequired('agentId', 'createAgentCompletionStreamRaw', agentId);

      validateRequired('compatibilityMode', 'createAgentCompletionStreamRaw', compatibilityMode);

      validateRequired('agentCompletionRequest', 'createAgentCompletionStreamRaw', agentCompletionRequest);

      const requestPath = '/agent-studio/1/agents/{agentId}/completions'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (compatibilityMode !== undefined) {
        queryParameters['compatibilityMode'] = compatibilityMode.toString();
      }

      if (stream !== undefined) {
        queryParameters['stream'] = stream.toString();
      }

      if (cache !== undefined) {
        queryParameters['cache'] = cache.toString();
      }

      if (memory !== undefined) {
        queryParameters['memory'] = memory.toString();
      }

      if (analytics !== undefined) {
        queryParameters['analytics'] = analytics.toString();
      }

      if (xAlgoliaSecureUserToken !== undefined) {
        headers['X-Algolia-Secure-User-Token'] = xAlgoliaSecureUserToken.toString();
      }

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: agentCompletionRequest,
      };

      return transporter.requestStream(request, requestOptions);
    },
    /**
     * Create a completion for the specified agent.  This endpoint handles two types of requests: 1. Normal completion request: User message -> Agent response 2. Tool approval response: User approval -> Execute tool -> Agent response  Tool Approval Flow (for MCP tools with requiresApproval: true): - Request 1: User sends message -> Agent requests tool call -> Return approval request - Request 2: User approves -> Execute tool -> Agent continues with result. (streaming version).
     *
     * Yields {@link StreamEvent} objects wrapping parsed `{ [key: string]: any; }` payloads.
     *
     * @see createAgentCompletionStreamRaw for the raw variant.
     * @see createAgentCompletion for the non-streaming version.
     */
    async *createAgentCompletionStream(
      {
        agentId,
        compatibilityMode,
        agentCompletionRequest,
        stream,
        cache,
        memory,
        analytics,
        xAlgoliaSecureUserToken,
      }: CreateAgentCompletionProps,
      requestOptions?: RequestOptions,
    ): AsyncGenerator<StreamEvent<{ [key: string]: any }>> {
      for await (const event of this.createAgentCompletionStreamRaw(
        {
          agentId,
          compatibilityMode,
          agentCompletionRequest,
          stream,
          cache,
          memory,
          analytics,
          xAlgoliaSecureUserToken,
        },
        requestOptions,
      )) {
        try {
          const data = JSON.parse(event.data) as { [key: string]: any };
          yield { data, raw: event };
        } catch (e) {
          yield { data: null, raw: event, error: e as Error };
        }
      }
    },

    /**
     * Create new feedback entry.
     *
     * Required API Key ACLs:
     *  - search
     * @param feedbackCreationRequest - The feedbackCreationRequest object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createFeedback(
      feedbackCreationRequest: FeedbackCreationRequest,
      requestOptions?: RequestOptions,
    ): Promise<FeedbackResponse> {
      validateRequired('feedbackCreationRequest', 'createFeedback', feedbackCreationRequest);

      validateRequired('feedbackCreationRequest.messageId', 'createFeedback', feedbackCreationRequest.messageId);
      validateRequired('feedbackCreationRequest.agentId', 'createFeedback', feedbackCreationRequest.agentId);
      validateRequired('feedbackCreationRequest.vote', 'createFeedback', feedbackCreationRequest.vote);

      const requestPath = '/agent-studio/1/feedback';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: feedbackCreationRequest,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Create Provider.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param providerAuthenticationCreate - The providerAuthenticationCreate object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createProvider(
      providerAuthenticationCreate: ProviderAuthenticationCreate,
      requestOptions?: RequestOptions,
    ): Promise<ProviderAuthenticationResponse> {
      validateRequired('providerAuthenticationCreate', 'createProvider', providerAuthenticationCreate);

      validateRequired('providerAuthenticationCreate.name', 'createProvider', providerAuthenticationCreate.name);
      validateRequired(
        'providerAuthenticationCreate.providerName',
        'createProvider',
        providerAuthenticationCreate.providerName,
      );
      validateRequired('providerAuthenticationCreate.input', 'createProvider', providerAuthenticationCreate.input);

      const requestPath = '/agent-studio/1/providers';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: providerAuthenticationCreate,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Create Secret Key.
     *
     * Required API Key ACLs:
     *  - admin
     * @param secretKeyCreate - The secretKeyCreate object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    createSecretKey(secretKeyCreate: SecretKeyCreate, requestOptions?: RequestOptions): Promise<SecretKeyResponse> {
      validateRequired('secretKeyCreate', 'createSecretKey', secretKeyCreate);

      validateRequired('secretKeyCreate.name', 'createSecretKey', secretKeyCreate.name);

      const requestPath = '/agent-studio/1/secret-keys';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: secretKeyCreate,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customDelete - The customDelete object.
     * @param customDelete.path - Path of the endpoint, for example `1/newFeature`.
     * @param customDelete.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customDelete(
      { path, parameters }: CustomDeleteProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customDelete', path);

      const requestPath = '/agent-studio/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customGet - The customGet object.
     * @param customGet.path - Path of the endpoint, for example `1/newFeature`.
     * @param customGet.parameters - Query parameters to apply to the current query.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customGet({ path, parameters }: CustomGetProps, requestOptions?: RequestOptions): Promise<Record<string, unknown>> {
      validateRequired('path', 'customGet', path);

      const requestPath = '/agent-studio/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customPost - The customPost object.
     * @param customPost.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPost.parameters - Query parameters to apply to the current query.
     * @param customPost.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customPost(
      { path, parameters, body }: CustomPostProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customPost', path);

      const requestPath = '/agent-studio/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * This method lets you send requests to the Algolia REST API.
     * @param customPut - The customPut object.
     * @param customPut.path - Path of the endpoint, for example `1/newFeature`.
     * @param customPut.parameters - Query parameters to apply to the current query.
     * @param customPut.body - Parameters to send with the custom request.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    customPut(
      { path, parameters, body }: CustomPutProps,
      requestOptions?: RequestOptions,
    ): Promise<Record<string, unknown>> {
      validateRequired('path', 'customPut', path);

      const requestPath = '/agent-studio/{path}'.replace('{path}', path);
      const headers: Headers = {};
      const queryParameters: QueryParameters = parameters ? parameters : {};

      const request: Request = {
        method: 'PUT',
        path: requestPath,
        queryParameters,
        headers,
        data: body ? body : {},
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Delete the specified agent.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteAgent - The deleteAgent object.
     * @param deleteAgent.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteAgent({ agentId }: DeleteAgentProps, requestOptions?: RequestOptions): Promise<void> {
      validateRequired('agentId', 'deleteAgent', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}'.replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Deletes the conversations matching the given filers.
     *
     * Required API Key ACLs:
     *  - logs
     * @param deleteAgentConversations - The deleteAgentConversations object.
     * @param deleteAgentConversations.agentId - The agentId.
     * @param deleteAgentConversations.startDate - Filter conversations created after this date (format: YYYY-MM-DD).
     * @param deleteAgentConversations.endDate - Filter conversations created before this date (format: YYYY-MM-DD).
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteAgentConversations(
      { agentId, startDate, endDate }: DeleteAgentConversationsProps,
      requestOptions?: RequestOptions,
    ): Promise<void> {
      validateRequired('agentId', 'deleteAgentConversations', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/conversations'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (startDate !== undefined) {
        queryParameters['startDate'] = startDate.toString();
      }

      if (endDate !== undefined) {
        queryParameters['endDate'] = endDate.toString();
      }

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Remove an allowed domain by id.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteAllowedDomain - The deleteAllowedDomain object.
     * @param deleteAllowedDomain.domainId - The domainId.
     * @param deleteAllowedDomain.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteAllowedDomain(
      { domainId, agentId }: DeleteAllowedDomainProps,
      requestOptions?: RequestOptions,
    ): Promise<void> {
      validateRequired('domainId', 'deleteAllowedDomain', domainId);

      validateRequired('agentId', 'deleteAllowedDomain', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains/{domainId}'
        .replace('{domainId}', encodeURIComponent(domainId))
        .replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Deletes the conversation with the given ID.
     *
     * Required API Key ACLs:
     *  - logs
     * @param deleteConversation - The deleteConversation object.
     * @param deleteConversation.conversationId - The conversationId.
     * @param deleteConversation.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteConversation(
      { conversationId, agentId }: DeleteConversationProps,
      requestOptions?: RequestOptions,
    ): Promise<void> {
      validateRequired('conversationId', 'deleteConversation', conversationId);

      validateRequired('agentId', 'deleteConversation', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/conversations/{conversationId}'
        .replace('{conversationId}', encodeURIComponent(conversationId))
        .replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Delete Provider.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param deleteProvider - The deleteProvider object.
     * @param deleteProvider.providerId - The providerId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteProvider({ providerId }: DeleteProviderProps, requestOptions?: RequestOptions): Promise<void> {
      validateRequired('providerId', 'deleteProvider', providerId);

      const requestPath = '/agent-studio/1/providers/{providerId}'.replace(
        '{providerId}',
        encodeURIComponent(providerId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Delete Secret Key.
     *
     * Required API Key ACLs:
     *  - admin
     * @param deleteSecretKey - The deleteSecretKey object.
     * @param deleteSecretKey.secretKeyId - The secretKeyId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteSecretKey({ secretKeyId }: DeleteSecretKeyProps, requestOptions?: RequestOptions): Promise<void> {
      validateRequired('secretKeyId', 'deleteSecretKey', secretKeyId);

      const requestPath = '/agent-studio/1/secret-keys/{secretKeyId}'.replace(
        '{secretKeyId}',
        encodeURIComponent(secretKeyId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Permanently deletes all messages for the given user token. Does not delete conversations.
     *
     * Required API Key ACLs:
     *  - logs
     * @param deleteUserData - The deleteUserData object.
     * @param deleteUserData.userToken - The userToken.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    deleteUserData({ userToken }: DeleteUserDataProps, requestOptions?: RequestOptions): Promise<void> {
      validateRequired('userToken', 'deleteUserData', userToken);

      const requestPath = '/agent-studio/1/user-data/{userToken}'.replace('{userToken}', encodeURIComponent(userToken));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Exports all conversations based on the passed filters.
     *
     * Required API Key ACLs:
     *  - logs
     * @param exportConversations - The exportConversations object.
     * @param exportConversations.agentId - The agentId.
     * @param exportConversations.startDate - Filter conversations created after this date (format: YYYY-MM-DD).
     * @param exportConversations.endDate - Filter conversations created before this date (format: YYYY-MM-DD).
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    exportConversations(
      { agentId, startDate, endDate }: ExportConversationsProps,
      requestOptions?: RequestOptions,
    ): Promise<Array<ConversationFullResponse>> {
      validateRequired('agentId', 'exportConversations', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/conversations/export'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (startDate !== undefined) {
        queryParameters['startDate'] = startDate.toString();
      }

      if (endDate !== undefined) {
        queryParameters['endDate'] = endDate.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Retrieve details of the specified agent.
     *
     * Required API Key ACLs:
     *  - settings
     * @param getAgent - The getAgent object.
     * @param getAgent.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getAgent({ agentId }: GetAgentProps, requestOptions?: RequestOptions): Promise<AgentWithVersionResponse> {
      validateRequired('agentId', 'getAgent', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}'.replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get a single allowed domain by id.
     *
     * Required API Key ACLs:
     *  - settings
     * @param getAllowedDomain - The getAllowedDomain object.
     * @param getAllowedDomain.domainId - The domainId.
     * @param getAllowedDomain.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getAllowedDomain(
      { domainId, agentId }: GetAllowedDomainProps,
      requestOptions?: RequestOptions,
    ): Promise<AllowedDomainResponse> {
      validateRequired('domainId', 'getAllowedDomain', domainId);

      validateRequired('agentId', 'getAllowedDomain', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains/{domainId}'
        .replace('{domainId}', encodeURIComponent(domainId))
        .replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get Configuration.
     *
     * Required API Key ACLs:
     *  - logs
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getConfiguration(requestOptions?: RequestOptions | undefined): Promise<ApplicationConfigResponse> {
      const requestPath = '/agent-studio/1/configuration';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Retrieves the conversation and its messages for the given ID.
     *
     * Required API Key ACLs:
     *  - logs
     * @param getConversation - The getConversation object.
     * @param getConversation.conversationId - The conversationId.
     * @param getConversation.agentId - The agentId.
     * @param getConversation.includeFeedback - Include feedback for the conversation.
     * @param getConversation.xAlgoliaSecureUserToken - The X-Algolia-Secure-User-Token.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getConversation(
      { conversationId, agentId, includeFeedback, xAlgoliaSecureUserToken }: GetConversationProps,
      requestOptions?: RequestOptions,
    ): Promise<ConversationFullResponse> {
      validateRequired('conversationId', 'getConversation', conversationId);

      validateRequired('agentId', 'getConversation', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/conversations/{conversationId}'
        .replace('{conversationId}', encodeURIComponent(conversationId))
        .replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (includeFeedback !== undefined) {
        queryParameters['includeFeedback'] = includeFeedback.toString();
      }

      if (xAlgoliaSecureUserToken !== undefined) {
        headers['X-Algolia-Secure-User-Token'] = xAlgoliaSecureUserToken.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get Provider.
     *
     * Required API Key ACLs:
     *  - settings
     * @param getProvider - The getProvider object.
     * @param getProvider.providerId - The providerId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getProvider(
      { providerId }: GetProviderProps,
      requestOptions?: RequestOptions,
    ): Promise<ProviderAuthenticationResponse> {
      validateRequired('providerId', 'getProvider', providerId);

      const requestPath = '/agent-studio/1/providers/{providerId}'.replace(
        '{providerId}',
        encodeURIComponent(providerId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get Secret Key.
     *
     * Required API Key ACLs:
     *  - settings
     * @param getSecretKey - The getSecretKey object.
     * @param getSecretKey.secretKeyId - The secretKeyId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getSecretKey({ secretKeyId }: GetSecretKeyProps, requestOptions?: RequestOptions): Promise<SecretKeyResponse> {
      validateRequired('secretKeyId', 'getSecretKey', secretKeyId);

      const requestPath = '/agent-studio/1/secret-keys/{secretKeyId}'.replace(
        '{secretKeyId}',
        encodeURIComponent(secretKeyId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Retrieves all memories, conversations and their messages for the given user token.
     *
     * Required API Key ACLs:
     *  - logs
     * @param getUserData - The getUserData object.
     * @param getUserData.userToken - The userToken.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    getUserData({ userToken }: GetUserDataProps, requestOptions?: RequestOptions): Promise<UserDataResponse> {
      validateRequired('userToken', 'getUserData', userToken);

      const requestPath = '/agent-studio/1/user-data/{userToken}'.replace('{userToken}', encodeURIComponent(userToken));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Invalidate cached completions for this agent. Filter with `before` (exclusive).
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param invalidateAgentCache - The invalidateAgentCache object.
     * @param invalidateAgentCache.agentId - The agentId.
     * @param invalidateAgentCache.before - Delete entries strictly before this date (exclusive, YYYY-MM-DD).
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    invalidateAgentCache(
      { agentId, before }: InvalidateAgentCacheProps,
      requestOptions?: RequestOptions,
    ): Promise<void> {
      validateRequired('agentId', 'invalidateAgentCache', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/cache'.replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (before !== undefined) {
        queryParameters['before'] = before.toString();
      }

      const request: Request = {
        method: 'DELETE',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * List all allowed domain patterns for this agent.
     *
     * Required API Key ACLs:
     *  - settings
     * @param listAgentAllowedDomains - The listAgentAllowedDomains object.
     * @param listAgentAllowedDomains.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listAgentAllowedDomains(
      { agentId }: ListAgentAllowedDomainsProps,
      requestOptions?: RequestOptions,
    ): Promise<AllowedDomainListResponse> {
      validateRequired('agentId', 'listAgentAllowedDomains', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/allowed-domains'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Retrieves the conversations for the given agent ID.
     *
     * Required API Key ACLs:
     *  - logs
     * @param listAgentConversations - The listAgentConversations object.
     * @param listAgentConversations.agentId - The agentId.
     * @param listAgentConversations.startDate - Filter conversations created after this date (format: YYYY-MM-DD).
     * @param listAgentConversations.endDate - Filter conversations created before this date (format: YYYY-MM-DD).
     * @param listAgentConversations.includeFeedback - Include feedback per conversation.
     * @param listAgentConversations.feedbackVote - Filter by feedback value (requires includeFeedback=true).
     * @param listAgentConversations.page - Page number.
     * @param listAgentConversations.limit - Items per page.
     * @param listAgentConversations.xAlgoliaSecureUserToken - The X-Algolia-Secure-User-Token.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listAgentConversations(
      {
        agentId,
        startDate,
        endDate,
        includeFeedback,
        feedbackVote,
        page,
        limit,
        xAlgoliaSecureUserToken,
      }: ListAgentConversationsProps,
      requestOptions?: RequestOptions,
    ): Promise<PaginatedConversationsResponse> {
      validateRequired('agentId', 'listAgentConversations', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/conversations'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (startDate !== undefined) {
        queryParameters['startDate'] = startDate.toString();
      }

      if (endDate !== undefined) {
        queryParameters['endDate'] = endDate.toString();
      }

      if (includeFeedback !== undefined) {
        queryParameters['includeFeedback'] = includeFeedback.toString();
      }

      if (feedbackVote !== undefined) {
        queryParameters['feedbackVote'] = feedbackVote.toString();
      }

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (limit !== undefined) {
        queryParameters['limit'] = limit.toString();
      }

      if (xAlgoliaSecureUserToken !== undefined) {
        headers['X-Algolia-Secure-User-Token'] = xAlgoliaSecureUserToken.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * List all agents with pagination and filtering.
     *
     * Required API Key ACLs:
     *  - settings
     * @param listAgents - The listAgents object.
     * @param listAgents.page - Page number.
     * @param listAgents.limit - Items per page.
     * @param listAgents.providerId - Filter by provider id.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listAgents(
      { page, limit, providerId }: ListAgentsProps = {},
      requestOptions: RequestOptions | undefined = undefined,
    ): Promise<PaginatedAgentsResponse> {
      const requestPath = '/agent-studio/1/agents';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (limit !== undefined) {
        queryParameters['limit'] = limit.toString();
      }

      if (providerId !== undefined) {
        queryParameters['providerId'] = providerId.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get Provider Models.
     *
     * Required API Key ACLs:
     *  - settings
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listModels(requestOptions?: RequestOptions | undefined): Promise<{ [key: string]: Array<string> }> {
      const requestPath = '/agent-studio/1/providers/models';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Get available models for a specific provider.
     *
     * Required API Key ACLs:
     *  - settings
     * @param listProviderModels - The listProviderModels object.
     * @param listProviderModels.providerId - The providerId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listProviderModels(
      { providerId }: ListProviderModelsProps,
      requestOptions?: RequestOptions,
    ): Promise<Array<string>> {
      validateRequired('providerId', 'listProviderModels', providerId);

      const requestPath = '/agent-studio/1/providers/{providerId}/models'.replace(
        '{providerId}',
        encodeURIComponent(providerId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * List Providers.
     *
     * Required API Key ACLs:
     *  - settings
     * @param listProviders - The listProviders object.
     * @param listProviders.page - Page number.
     * @param listProviders.limit - Items per page.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listProviders(
      { page, limit }: ListProvidersProps = {},
      requestOptions: RequestOptions | undefined = undefined,
    ): Promise<PaginatedProviderAuthenticationsResponse> {
      const requestPath = '/agent-studio/1/providers';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (limit !== undefined) {
        queryParameters['limit'] = limit.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * List Secret Keys.
     *
     * Required API Key ACLs:
     *  - settings
     * @param listSecretKeys - The listSecretKeys object.
     * @param listSecretKeys.page - Page number.
     * @param listSecretKeys.limit - Items per page.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    listSecretKeys(
      { page, limit }: ListSecretKeysProps = {},
      requestOptions: RequestOptions | undefined = undefined,
    ): Promise<PaginatedSecretKeysResponse> {
      const requestPath = '/agent-studio/1/secret-keys';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      if (page !== undefined) {
        queryParameters['page'] = page.toString();
      }

      if (limit !== undefined) {
        queryParameters['limit'] = limit.toString();
      }

      const request: Request = {
        method: 'GET',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Publish the specified agent.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param publishAgent - The publishAgent object.
     * @param publishAgent.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    publishAgent({ agentId }: PublishAgentProps, requestOptions?: RequestOptions): Promise<AgentWithVersionResponse> {
      validateRequired('agentId', 'publishAgent', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/publish'.replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Unpublish the specified agent.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param unpublishAgent - The unpublishAgent object.
     * @param unpublishAgent.agentId - The agentId.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    unpublishAgent(
      { agentId }: UnpublishAgentProps,
      requestOptions?: RequestOptions,
    ): Promise<AgentWithVersionResponse> {
      validateRequired('agentId', 'unpublishAgent', agentId);

      const requestPath = '/agent-studio/1/agents/{agentId}/unpublish'.replace(
        '{agentId}',
        encodeURIComponent(agentId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'POST',
        path: requestPath,
        queryParameters,
        headers,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Update the specified agent.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param updateAgent - The updateAgent object.
     * @param updateAgent.agentId - The agentId.
     * @param updateAgent.agentConfigUpdate - The agentConfigUpdate object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    updateAgent(
      { agentId, agentConfigUpdate }: UpdateAgentProps,
      requestOptions?: RequestOptions,
    ): Promise<AgentWithVersionResponse> {
      validateRequired('agentId', 'updateAgent', agentId);

      validateRequired('agentConfigUpdate', 'updateAgent', agentConfigUpdate);

      const requestPath = '/agent-studio/1/agents/{agentId}'.replace('{agentId}', encodeURIComponent(agentId));
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PATCH',
        path: requestPath,
        queryParameters,
        headers,
        data: agentConfigUpdate,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Patch Configuration.
     *
     * Required API Key ACLs:
     *  - logs
     * @param applicationConfigPatch - The applicationConfigPatch object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    updateConfiguration(
      applicationConfigPatch: ApplicationConfigPatch,
      requestOptions?: RequestOptions,
    ): Promise<ApplicationConfigResponse> {
      validateRequired('applicationConfigPatch', 'updateConfiguration', applicationConfigPatch);

      const requestPath = '/agent-studio/1/configuration';
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PATCH',
        path: requestPath,
        queryParameters,
        headers,
        data: applicationConfigPatch,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Update Provider.
     *
     * Required API Key ACLs:
     *  - editSettings
     * @param updateProvider - The updateProvider object.
     * @param updateProvider.providerId - The providerId.
     * @param updateProvider.providerAuthenticationPatch - The providerAuthenticationPatch object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    updateProvider(
      { providerId, providerAuthenticationPatch }: UpdateProviderProps,
      requestOptions?: RequestOptions,
    ): Promise<ProviderAuthenticationResponse> {
      validateRequired('providerId', 'updateProvider', providerId);

      validateRequired('providerAuthenticationPatch', 'updateProvider', providerAuthenticationPatch);

      const requestPath = '/agent-studio/1/providers/{providerId}'.replace(
        '{providerId}',
        encodeURIComponent(providerId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PATCH',
        path: requestPath,
        queryParameters,
        headers,
        data: providerAuthenticationPatch,
      };

      return transporter.request(request, requestOptions);
    },

    /**
     * Patch Secret Key.
     *
     * Required API Key ACLs:
     *  - admin
     * @param updateSecretKey - The updateSecretKey object.
     * @param updateSecretKey.secretKeyId - The secretKeyId.
     * @param updateSecretKey.secretKeyPatch - The secretKeyPatch object.
     * @param requestOptions - The requestOptions to send along with the query, they will be merged with the transporter requestOptions.
     */
    updateSecretKey(
      { secretKeyId, secretKeyPatch }: UpdateSecretKeyProps,
      requestOptions?: RequestOptions,
    ): Promise<SecretKeyResponse> {
      validateRequired('secretKeyId', 'updateSecretKey', secretKeyId);

      validateRequired('secretKeyPatch', 'updateSecretKey', secretKeyPatch);

      const requestPath = '/agent-studio/1/secret-keys/{secretKeyId}'.replace(
        '{secretKeyId}',
        encodeURIComponent(secretKeyId),
      );
      const headers: Headers = {};
      const queryParameters: QueryParameters = {};

      const request: Request = {
        method: 'PATCH',
        path: requestPath,
        queryParameters,
        headers,
        data: secretKeyPatch,
      };

      return transporter.request(request, requestOptions);
    },
  };
}
